import './Checkbox.scss'

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import Icon from '../Icon/Icon'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { formEvent } from '../Form/Form'
import t from '../../helpers/i18n'

const PROPAGATE_UP = 1
const PROPAGATE_DOWN = 2
const PROPAGATE_BOTH = PROPAGATE_UP | PROPAGATE_DOWN

const Checkbox = ({ name, label, labelPosition, highlighted, help, initialValue, grow, disabled, readOnly, group, manager, onChange }) => {
  const _initialValue = initialValue !== '0' && initialValue
  const [checked, setChecked] = useState(_initialValue)
  const [partial, setPartial] = useState(false)
  const element = useRef()

  const propagateChecked = useCallback(
    (isChecked, isPartial, direction) => {
      setChecked(isChecked)
      setPartial(isPartial)
      if (manager && direction & PROPAGATE_DOWN) {
        element.current.dispatchEvent(
          checkboxEvent({
            action: 'GROUP-CHECKED',
            checked: isChecked
          })
        )
      }
      if (group && direction & PROPAGATE_UP) {
        element.current.dispatchEvent(
          checkboxEvent({
            action: 'CHECKED',
            group,
            name,
            checked: isChecked,
            partial: isPartial,
            propagate: propagateChecked
          })
        )
      }
    },
    [group, manager, name]
  )

  const handleChange = useCallback(() => {
    if (!disabled && !readOnly) {
      propagateChecked(!checked, false, PROPAGATE_BOTH)
    }
  }, [disabled, readOnly, checked, propagateChecked])

  const handleKeyPress = useCallback(
    (event) => {
      if (event.keyCode === 13 || event.keyCode === 32) {
        event.stopPropagation()
        handleChange()
      }
    },
    [handleChange]
  )

  useEffect(() => {
    if (onChange) onChange(checked)
  }, [onChange, checked])

  useEffect(() => {
    if (name) {
      element.current.dispatchEvent(
        formEvent({
          action: 'UPDATE',
          name,
          value: checked
        })
      )
    }
  }, [checked, name])

  useEffect(() => {
    propagateChecked(_initialValue, false, PROPAGATE_UP)
  }, [propagateChecked, _initialValue])

  useEffect(() => {
    if (manager) {
      element.current.dispatchEvent(
        checkboxEvent({
          action: 'MANAGER',
          group,
          propagate: propagateChecked
        })
      )
    }
  }, [group, manager, propagateChecked])

  useEffect(() => {
    const _element = element.current
    return () => {
      if (name) {
        _element.dispatchEvent(
          formEvent({
            action: 'REMOVE',
            name
          })
        )
        if (group) {
          _element.dispatchEvent(
            checkboxEvent({
              action: 'REMOVE',
              name
            })
          )
        }
      }
    }
  }, [name, group])

  return (
    <div ref={element} className={classNames("checkbox", {
      "-checked": checked,
      "-enabled": !disabled && !readOnly,
      "-read-only": !disabled && readOnly,
      "-disabled": disabled,
      '-inverted': labelPosition === 'left',
      '-grow': grow
    })}>
      <div
        className={classNames('-box')}
        tabIndex='0'
        onClick={handleChange}
        onKeyUp={handleKeyPress}
      >
        {checked &&
          <Icon
            size={14}
            icon={partial ? 'MinusIcon' : 'CheckmarkIcon'}
          />
        }
      </div>
      {label &&
        <label className={classNames("text-label", {
          "label-field-sized-basic": !highlighted && !checked,
          "label-field-sized-medium": !highlighted && checked,
          "overline-label-large-bold": highlighted
        })}>
          {label}
        </label>
      }
      {help && (
        <div className='-help'>
          <Icon size={14} icon='HelpIcon' />
        </div>
      )}
    </div>
  )
}

const CheckboxGroup = ({ group, children }) => {
  const checkboxGroupRef = useRef()
  const checkGroup = useRef({})
  const managerUpdate = useRef()

  const updateManager = useCallback(() => {
    const isPartial = Object.keys(checkGroup.current).find(
      (key) => checkGroup.current[key].partial
    )
    const isChecked = !Object.keys(checkGroup.current).find(
      (key) => !checkGroup.current[key].checked
    )
    const isNotChecked = !Object.keys(checkGroup.current).find(
      (key) => checkGroup.current[key].checked
    )
    const checked = !isPartial && (isChecked || isNotChecked) ? isChecked : true
    const partial = !(!isPartial && (isChecked || isNotChecked))
    if (managerUpdate.current)
      managerUpdate.current.propagate(checked, partial, PROPAGATE_UP)
  }, [checkGroup])

  const updateCheckboxesGroup = useCallback(
    (isChecked) => {
      Object.keys(checkGroup.current).forEach((key) => {
        checkGroup.current[key].propagate(isChecked, false, PROPAGATE_DOWN)
        checkGroup.current[key].checked = isChecked
        checkGroup.current[key].partial = false
      })
    },
    [checkGroup]
  )

  const handleCheckboxChange = useCallback(
    (name, checked, partial, propagate) => {
      checkGroup.current[name] = { checked, partial, propagate }
      updateManager()
    },
    [checkGroup, updateManager]
  )

  const handleCheckboxRemove = useCallback(
    (name) => {
      delete checkGroup.current[name]
      updateManager()
    },
    [checkGroup, updateManager]
  )

  const handleCheckboxManager = useCallback(
    (propagate) => {
      managerUpdate.current = { propagate }
    },
    [managerUpdate]
  )

  const handleCheckboxGroupChange = useCallback(
    (checked) => {
      updateCheckboxesGroup(checked)
    },
    [updateCheckboxesGroup]
  )

  useLayoutEffect(() => {
    const eventListener = (event) => {
      if (event.detail.group === group) {
        if (event.detail.action === 'CHECKED') {
          handleCheckboxChange(
            event.detail.name,
            event.detail.checked,
            event.detail.partial,
            event.detail.propagate
          )
        } else if (event.detail.action === 'REMOVE') {
          handleCheckboxRemove(event.detail.name)
        } else if (event.detail.action === 'MANAGER') {
          throw new Error(
            t('Cannot be group manager of itself, check the group attribute.')
          )
        } else {
          throw new Error(t('Event not supported: ') + event.detail.action)
        }
        event.stopPropagation()
      } else if (event.detail.action === 'GROUP-CHECKED') {
        handleCheckboxGroupChange(event.detail.checked)
        event.stopPropagation()
      } else if (event.detail.action === 'MANAGER') {
        handleCheckboxManager(event.detail.propagate)
        event.stopPropagation()
      }
    }
    const _element = checkboxGroupRef.current
    _element.addEventListener('CheckboxGroupEvent', eventListener, false)
    return () => {
      _element.removeEventListener('CheckboxGroupEvent', eventListener, false)
    }
  }, [
    group,
    handleCheckboxChange,
    handleCheckboxRemove,
    handleCheckboxManager,
    handleCheckboxGroupChange
  ])

  return <div ref={checkboxGroupRef}>{children}</div>
}

const checkboxEvent = (params) => {
  return new CustomEvent('CheckboxGroupEvent', {
    bubbles: true,
    cancelable: true,
    detail: { ...params }
  })
}

Checkbox.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  help: PropTypes.string,
  highlighted: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  labelPosition: PropTypes.oneOf(['right', 'left']),
  onChange: PropTypes.func
}

Checkbox.defaultProps = {
  labelPosition: 'right'
}

export default Checkbox
export { CheckboxGroup }
